// Pagination helpers extracted from OfferPageBuilderSimple.js lines 1252-1853
import { useCallback, useEffect } from 'react';
import { ELEMENT_TYPES } from '../../../utils/offerElementTypes';
import {
  BRANDS_PAGE_MAX_HEIGHT,
} from '../../../utils/offerDeviceConstants';
import { DEFAULT_ELEMENTS } from '../../../utils/offerDefaultElements';

const MOBILE_PHONE_HEIGHT = 700; // Standard mobile phone height for pagination

export const usePagination = ({
  pages,
  setPages,
  currentPageIndex,
  setCurrentPageIndex,
  nextPageId,
  setNextPageId,
  paginationEnabled,
  setPaginationEnabled,
  isEditingMode,
  elements,
  localElements,
  isBrandsPage,
  sharedElements,
  selectedElement,
  setSelectedElement,
  setTabValue,
  setRightPanelOpen,
  setElements,
  insertAfterElementId,
  setInsertAfterElementId,
  setShowLayoutSelector,
}) => {
  const calculateElementHeight = useCallback((element) => {
    // Base height estimates for different element types
    const baseHeights = {
      [ELEMENT_TYPES.HEADING]: 60,
      [ELEMENT_TYPES.TEXT]: 80,
      [ELEMENT_TYPES.IMAGE]: 200,
      [ELEMENT_TYPES.CROPPABLE_IMAGE]: 200,
      [ELEMENT_TYPES.FITTABLE_IMAGE]: 200,
      [ELEMENT_TYPES.CIRCULAR_IMAGE]: 200,
      [ELEMENT_TYPES.BUTTON]: 50,
      [ELEMENT_TYPES.VIDEO]: 250,
      [ELEMENT_TYPES.HTML]: 100,
      [ELEMENT_TYPES.DIVIDER]: 20,
      [ELEMENT_TYPES.LAYOUT]: 150,
      [ELEMENT_TYPES.CHECKLIST]: 120,
      [ELEMENT_TYPES.BUTTON_WITH_NOTICE]: 100,
      [ELEMENT_TYPES.BUTTON_IMAGE_CALLOUT]: 200,
      [ELEMENT_TYPES.BUTTON_TEXT_VARIANT]: 80,
      [ELEMENT_TYPES.BUTTON_HEADLINE_PRODUCT_IMAGE]: 300,
      [ELEMENT_TYPES.CUSTOM_LIST]: 150,
      [ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE]: 200,
      [ELEMENT_TYPES.TIMELINE_LIST]: 250,
      [ELEMENT_TYPES.PROCESS_STEPS]: 300,
      [ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE]: 250,
      [ELEMENT_TYPES.LEFT_IMAGE_TEXT]: 250,
      [ELEMENT_TYPES.QUOTATION_BLOCK]: 150,
      [ELEMENT_TYPES.FAQ_ACCORDION]: 200,
      [ELEMENT_TYPES.COMPARISON_TABLE]: 300,
      [ELEMENT_TYPES.TWO_COLUMN_COMPARISON]: 250
    };

    let height = baseHeights[element.type] || 100;

    // Adjust height based on content length for text elements
    if (element.type === ELEMENT_TYPES.TEXT || element.type === ELEMENT_TYPES.HEADING) {
      const contentLength = element.content?.length || 0;
      const lines = Math.ceil(contentLength / 50); // Rough estimate of lines
      height = Math.max(height, lines * 25);
    }

    // Adjust height for list elements based on number of items
    if (element.type === ELEMENT_TYPES.CUSTOM_LIST || element.type === ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE) {
      const itemCount = element.rows?.length || 0;
      height = Math.max(height, itemCount * 40 + 60); // 40px per item + 60px base
    }

    // Adjust height for FAQ accordion based on number of items
    if (element.type === ELEMENT_TYPES.FAQ_ACCORDION) {
      const itemCount = element.items?.length || 0;
      height = Math.max(height, itemCount * 60 + 80); // 60px per item + 80px base
    }

    // Adjust height for timeline list based on number of steps
    if (element.type === ELEMENT_TYPES.TIMELINE_LIST) {
      const stepCount = element.steps?.length || 0;
      height = Math.max(height, stepCount * 80 + 100); // 80px per step + 100px base
    }

    // Adjust height for process steps based on number of steps
    if (element.type === ELEMENT_TYPES.PROCESS_STEPS) {
      const stepCount = element.steps?.length || 0;
      height = Math.max(height, stepCount * 100 + 120); // 100px per step + 120px base
    }

    // Adjust height for layout elements based on zones - recursively calculate
    if ((element.type === ELEMENT_TYPES.LAYOUT || element.type === ELEMENT_TYPES.IMAGE_OVERLAY) && element.zones) {
      // Find the tallest zone by recursively calculating heights of elements within zones
      const zoneHeights = element.zones.map(zone => {
        return (zone.elements || []).reduce((total, el) => {
          return total + calculateElementHeight(el) + 10; // 10px margin in zones
        }, 20); // 20px padding in zones
      });
      height = Math.max(100, ...zoneHeights); // At least 100px
    }

    return height;
  }, []);

  const calculatePageHeight = useCallback((pageElements) => {
    return pageElements.reduce((total, element) => {
      return total + calculateElementHeight(element) + 20; // Add 20px margin between elements
    }, 40); // Add 40px for page padding
  }, [calculateElementHeight]);

  // Check if brands page content exceeds height limit
  const checkBrandsPageOverflow = useCallback(() => {
    if (!isBrandsPage) return false;

    // Calculate total content height
    const totalHeight = calculatePageHeight(elements);
    return totalHeight > BRANDS_PAGE_MAX_HEIGHT;
  }, [isBrandsPage, elements, calculatePageHeight]);

  // Get brands page overflow info
  const getBrandsPageOverflowInfo = useCallback(() => {
    if (!isBrandsPage) return null;

    const totalHeight = calculatePageHeight(elements);
    const overflowHeight = totalHeight - BRANDS_PAGE_MAX_HEIGHT;

    if (overflowHeight > 0) {
      return {
        hasOverflow: true,
        totalHeight,
        maxHeight: BRANDS_PAGE_MAX_HEIGHT,
        overflowHeight
      };
    }

    return null;
  }, [isBrandsPage, elements, calculatePageHeight]);

  const getMaxPageHeight = useCallback(() => {
    // Mobile phone height minus bottom bar (60px) and content padding (32px total)
    return MOBILE_PHONE_HEIGHT - 92; // 60px bottom bar + 32px content padding
  }, []);

  const checkPageOverflow = useCallback((pageElements, newElement = null) => {
    const elementsToCheck = newElement ? [...pageElements, newElement] : pageElements;
    const pageHeight = calculatePageHeight(elementsToCheck);
    const maxHeight = getMaxPageHeight();
    const willOverflow = pageHeight > maxHeight;

    console.log('🔍 Overflow check:', {
      elementCount: elementsToCheck.length,
      pageHeight,
      maxHeight,
      willOverflow,
      elements: elementsToCheck.map(el => ({
        type: el.type,
        height: calculateElementHeight(el),
        rows: el.rows?.length || 0,
        items: el.items?.length || 0,
        steps: el.steps?.length || 0
      }))
    });

    return willOverflow;
  }, [calculatePageHeight, getMaxPageHeight]);

  // Check if elements are too long - DISABLED (manual pagination only)
  const checkAndEnablePagination = useCallback((elementsToCheck) => {
    if (!elementsToCheck || elementsToCheck.length === 0) return;

    // AUTO-PAGINATION COMPLETELY DISABLED
    // Users must manually enable pagination and split pages
    console.log('ℹ️ Auto-pagination disabled - users must manually enable pagination');
    return;
  }, []);

  // Long elements check DISABLED - manual pagination only
  useEffect(() => {
    // AUTO-PAGINATION COMPLETELY DISABLED
    // Users must manually enable pagination and split pages
    console.log('ℹ️ Long elements check disabled - manual pagination only');
  }, []);

  // Debug effect to track pagination state changes
  useEffect(() => {
    console.log('🔄 Pagination state changed:', {
      paginationEnabled,
      pagesCount: pages.length,
      currentPageIndex,
      elementsCount: elements.length
    });
  }, [paginationEnabled, pages.length, currentPageIndex, elements.length]);

  // Manual pagination toggle function
  const togglePagination = useCallback(() => {
    console.log('🔄 togglePagination clicked!', {
      paginationEnabled,
      elementsLength: elements.length,
      localElementsLength: localElements.length,
      pagesLength: pages.length,
      isBrandsPage,
      isEditingMode
    });

    if (paginationEnabled) {
      // Disable pagination - merge all pages into one
      const allElements = pages.flatMap(page => page.elements);
      setPaginationEnabled(false);
      setPages([{ id: 1, elements: allElements }]);
      setCurrentPageIndex(0);
      setNextPageId(2);
      console.log('✅ Pagination disabled, all elements merged into single page');
    } else {
      // Enable pagination - start with current elements as single page
      // Users will manually split pages using the split button
      const elementsToUse = elements.length > 0 ? elements : localElements;
      console.log('🔍 Elements to use:', elementsToUse.length, 'elements');

      if (elementsToUse.length > 0) {
        setPaginationEnabled(true);
        setPages([{ id: 1, elements: elementsToUse }]);
        setCurrentPageIndex(0);
        setNextPageId(2);
        console.log('✅ Pagination enabled - use manual split to create pages');
      } else {
        console.log('⚠️ No elements found to enable pagination');
      }
    }
  }, [paginationEnabled, pages, elements, localElements, isBrandsPage, isEditingMode]);

  // Manual page split function - split current page at a specific element
  const splitPageAtElement = useCallback((elementId) => {
    if (!paginationEnabled) {
      console.log('🚫 Pagination not enabled - cannot split page');
      return;
    }

    const currentPage = pages[currentPageIndex];
    if (!currentPage) return;

    const elementIndex = currentPage.elements.findIndex(el => el.id === elementId);
    if (elementIndex === -1 || elementIndex === 0) {
      console.log('🚫 Cannot split at this element');
      return;
    }

    // Split the current page at the element
    const elementsBeforeSplit = currentPage.elements.slice(0, elementIndex);
    const elementsAfterSplit = currentPage.elements.slice(elementIndex);

    // Create new pages
    const newPages = [...pages];
    newPages[currentPageIndex] = { ...currentPage, elements: elementsBeforeSplit };
    newPages.splice(currentPageIndex + 1, 0, { id: nextPageId, elements: elementsAfterSplit });

    setPages(newPages);
    setNextPageId(prev => prev + 1);
    setCurrentPageIndex(currentPageIndex + 1); // Move to the new page

    // Open right panel for adding new elements.
    // Tab index 2 ("Add new element") only exists in brands mode; guard to
    // avoid an invalid MUI Tabs value in the Setup/template editor.
    if (isBrandsPage) {
      setTabValue(2); // "Add new element" tab
    }
    setRightPanelOpen(true);

    console.log('✅ Page split at element:', elementId);
  }, [paginationEnabled, isEditingMode, isBrandsPage, pages, currentPageIndex, nextPageId, setPages, setCurrentPageIndex, setNextPageId, setTabValue, setRightPanelOpen]);

  // Manual page merge function - merge current page with next page
  const mergeWithNextPage = useCallback(() => {
    if (!paginationEnabled) {
      console.log('🚫 Pagination not enabled - cannot merge pages');
      return;
    }

    if (!isEditingMode) {
      console.log('🚫 Not in editing mode - cannot merge pages');
      return;
    }

    if (currentPageIndex >= pages.length - 1) {
      console.log('🚫 No next page to merge with');
      return;
    }

    const currentPage = pages[currentPageIndex];
    const nextPage = pages[currentPageIndex + 1];

    // Merge elements
    const mergedElements = [...currentPage.elements, ...nextPage.elements];

    // Create new pages
    const newPages = [...pages];
    newPages[currentPageIndex] = { ...currentPage, elements: mergedElements };
    newPages.splice(currentPageIndex + 1, 1);

    setPages(newPages);

    console.log('✅ Merged current page with next page');
  }, [paginationEnabled, isEditingMode, pages, currentPageIndex]);

  const moveToNewPage = useCallback(() => {
    console.log('🔄 moveToNewPage function called');

    if (!paginationEnabled) {
      console.log('🚫 Pagination not enabled - cannot move to new page');
      return;
    }

    // More permissive check: allow if we have selectedElement and pagination is enabled
    // regardless of editing mode restrictions
    if (!selectedElement) {
      console.log('🚫 No element selected - cannot move to new page');
      return;
    }

    const currentPage = pages[currentPageIndex];
    if (!currentPage) {
      console.log('🚫 No current page found');
      return;
    }

    console.log('🔍 Current page elements:', currentPage.elements.map(el => ({ id: el.id, type: el.type })));

    const elementIndex = currentPage.elements.findIndex(el => el.id === selectedElement.id);
    console.log('🔍 Element index:', elementIndex, 'for selected element:', selectedElement.id);

    if (elementIndex === -1) {
      console.log('🚫 Selected element not found in current page');
      return;
    }

    // Get elements from selected element onwards
    const elementsToMove = currentPage.elements.slice(elementIndex);
    const elementsToKeep = currentPage.elements.slice(0, elementIndex);

    console.log('🔍 Elements to move:', elementsToMove.length, elementsToMove.map(el => ({ id: el.id, type: el.type })));
    console.log('🔍 Elements to keep:', elementsToKeep.length, elementsToKeep.map(el => ({ id: el.id, type: el.type })));

    if (elementsToMove.length === 0) {
      console.log('🚫 No elements to move');
      return;
    }

    // Create new page with elements to move
    const newPage = { id: nextPageId, elements: elementsToMove };

    // Update current page to keep only elements before the selected one
    const newPages = [...pages];
    newPages[currentPageIndex] = { ...currentPage, elements: elementsToKeep };

    // Insert new page after current page
    newPages.splice(currentPageIndex + 1, 0, newPage);

    console.log('🔍 New pages structure:', newPages.map((page, idx) => ({
      index: idx,
      id: page.id,
      elementsCount: page.elements.length
    })));

    setPages(newPages);
    setNextPageId(prev => prev + 1);
    setCurrentPageIndex(currentPageIndex + 1);

    console.log('✅ Moved elements to new page:', {
      elementsMoved: elementsToMove.length,
      elementsKept: elementsToKeep.length,
      newPageId: newPage.id,
      newCurrentPageIndex: currentPageIndex + 1
    });
  }, [paginationEnabled, selectedElement, pages, currentPageIndex, nextPageId]);

  // Add new empty page - similar to blog creation
  const addPage = useCallback(() => {
    console.log('📄 addPage function called');

    // Add a new empty page right after the current page
    setPages(prev => {
      const newPage = {
        id: nextPageId,
        elements: []
      };

      // Insert the new page right after the current page
      const newPages = [
        ...prev.slice(0, currentPageIndex + 1),
        newPage,
        ...prev.slice(currentPageIndex + 1)
      ];

      // Switch to the new page
      setTimeout(() => {
        setCurrentPageIndex(currentPageIndex + 1);

        // Deselect any current element to focus on the new page
        setSelectedElement(null);

        // Open right panel for adding new elements.
        // The "Add new element" tab (index 2) only exists in brands mode;
        // setting tabValue=2 elsewhere produces an invalid MUI Tabs value and
        // breaks the post-click render (which made "Add page" appear to do nothing).
        if (isBrandsPage) {
          setTabValue(2); // "Add new element" tab
        }
        setRightPanelOpen(true);
      }, 0);

      return newPages;
    });

    setNextPageId(prev => prev + 1);
    console.log('📄 Added new empty page after current page');
  }, [currentPageIndex, nextPageId, isBrandsPage, setSelectedElement, setTabValue, setRightPanelOpen, setPages, setNextPageId, setCurrentPageIndex]);

  // Page navigation functions
  const goToPage = useCallback((pageIndex) => {
    if (pageIndex >= 0 && pageIndex < pages.length) {
      setCurrentPageIndex(pageIndex);
    }
  }, [pages.length]);

  const goToNextPage = useCallback(() => {
    if (currentPageIndex < pages.length - 1) {
      setCurrentPageIndex(prev => prev + 1);
    }
  }, [currentPageIndex, pages.length]);

  const goToPreviousPage = useCallback(() => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
    }
  }, [currentPageIndex]);

  const deletePage = useCallback((pageIndex) => {
    if (pages.length > 1) {
      // Multiple pages: just remove the specified page
      setPages(prev => prev.filter((_, index) => index !== pageIndex));
      if (currentPageIndex >= pageIndex && currentPageIndex > 0) {
        setCurrentPageIndex(prev => prev - 1);
      }
    } else {
      // Only one page: clear its content instead of deleting
      setPages([{ id: 1, elements: [] }]);
      setCurrentPageIndex(0);
    }
  }, [pages.length, currentPageIndex]);

  const generateId = () => `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addElement = useCallback((elementType) => {
    // Special handling for Layout element - show layout selector dialog
    if (elementType === ELEMENT_TYPES.LAYOUT) {
      setShowLayoutSelector(true);
      return;
    }

    // In brands page mode, use FITTABLE_IMAGE instead of IMAGE for better zoom/positioning functionality
    let actualElementType = elementType;
    if (isBrandsPage && elementType === ELEMENT_TYPES.IMAGE) {
      actualElementType = ELEMENT_TYPES.FITTABLE_IMAGE;
    }

    const newElement = {
      id: generateId(),
      ...DEFAULT_ELEMENTS[actualElementType]
    };

    // Check if we should insert after a specific element (from add button click)
    let insertIndex = -1;
    let targetElementId = insertAfterElementId;

    // Clear the insertAfterElementId after using it
    if (targetElementId) {
      setInsertAfterElementId(null);
    }

    if (targetElementId) {
      // Find the target element's position in the current page
      const currentPage = pages[currentPageIndex] || pages[0];
      insertIndex = currentPage.elements.findIndex(el => el.id === targetElementId);
      if (insertIndex !== -1) {
        insertIndex = insertIndex + 1; // Insert right after target element
      }
    }

    // Always add to current page (no overflow detection)
    setPages(prevPages => {
      const safeIndex = Math.min(currentPageIndex, prevPages.length - 1);
      const currentPage = prevPages[safeIndex];

      // Insert after selected element if found, otherwise append to end
      let newPageElements;
      if (insertIndex !== -1 && insertIndex <= currentPage.elements.length) {
        newPageElements = [...currentPage.elements];
        newPageElements.splice(insertIndex, 0, newElement);
      } else {
        newPageElements = [...currentPage.elements, newElement];
      }

      // Update the current page with new elements
      const updatedPages = prevPages.map((page, index) =>
        index === safeIndex
          ? { ...page, elements: newPageElements }
          : page
      );

      return updatedPages;
    });

    // Update flat elements array for non-paginated views
    setElements(prev => [...prev, newElement]);

    console.log('✅ Element added to current page:', { elementType: actualElementType, pageIndex: currentPageIndex });
  }, [insertAfterElementId, pages, currentPageIndex, setPages, setElements, isBrandsPage]);

  return {
    calculateElementHeight,
    calculatePageHeight,
    checkBrandsPageOverflow,
    getBrandsPageOverflowInfo,
    getMaxPageHeight,
    checkPageOverflow,
    checkAndEnablePagination,
    togglePagination,
    splitPageAtElement,
    mergeWithNextPage,
    moveToNewPage,
    addPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    deletePage,
    addElement,
    MOBILE_PHONE_HEIGHT,
  };
};
